import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { observer } from 'mobx-react';

import 'react-tooltip/dist/react-tooltip.css';
import * as S from './SearchComponent.style';
import Image from '../../image/Image';
import * as Cookie from '../../../utils/Cookie';
import SearchWordItem from './section/historyWordsSection/searchWordItem/SearchWordItem';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';

import useClickOutside from '../../../hooks/useClickOutside';

import useEnterKeyEvent from '../../../hooks/useEnterKeyEvent';
import TooltipItem from '../../tooltipItem/TooltipItem';
import useTagListQuery from '../../../hooks/queries/useTagQuery';
import useTagClickHandler from '../../../hooks/useTagClickHandler';
import SearchDetailBox from './SearchDetailBox';

// 새로 추가된 import
import { useContentAutocomplete } from '../../../hooks/useContentAutocomplete';
import { useDebounce } from '../../../hooks/useDebounce';
import AutocompleteDropdown from '../../autocomplete/AutocompleteDropdown';

const SearchComponent = observer(() => {
	const location = useLocation();
	// const navigate = useNavigate();
	const searchInputRef = useRef(null);
	const playRegex = /^\/play($|\/)/;
	const { data: tags } = useTagListQuery();
	const [searchWordList, setSearchWordList] = useState([]);
	const [currentSearchWord, setCurrentSearchWord] = useState(null);
	const [isCookieHere, setIsCookieHere] = useState(false);
	const [profileName, setProfileName] = useState(null);
	const [wordHistoryCookie, SetWordHistoryCookie] = useCookies([
		'searchWordListHistory',
	]);
	const tagClickHandler = useTagClickHandler();

	const [suggestedTags, setSuggestedTags] = useState([]);
	const [blurTimeoutId, setBlurTimeoutId] = useState(null);

	const [isSearchInputFocus, setSearchInputFocus] = useState(false);
	const [isSearchBarClicked, setIsSearchBarClicked] = useState(false);
	const searchBarRef = useRef(null);

	const navigate = useNavigate();

	// 새로 추가된 상태들
	const [searchInput, setSearchInput] = useState('');
	const [isComposing, setIsComposing] = useState(false);

	// 검색어 디바운스
	// const debouncedSearchText = useDebounce(searchInput, 300);
	const debouncedSearchText = useDebounce('', 300);

	// 자동완성 훅
	const { autocomplete, isLoading, error } = useContentAutocomplete({
		searchText: debouncedSearchText,
	});

	// 검색 결과가 있을 때 드롭다운 자동 열기
	useEffect(() => {
		if (
			debouncedSearchText.length >= 2 &&
			autocomplete.filteredItems.length > 0 &&
			!isLoading
		) {
			autocomplete.setIsOpen(true);
		} else if (debouncedSearchText.length < 2) {
			autocomplete.setIsOpen(false);
		}
	}, [debouncedSearchText, autocomplete.filteredItems.length, isLoading]);

	useEffect(() => {
		if (searchInputRef.current?.value) {
			const similarTags = tags?.filter((tag) =>
				tag.name.includes(searchInputRef.current.value),
			);
			setSuggestedTags(similarTags);
		} else {
			setSuggestedTags([]);
		}
	}, [searchInputRef.current?.value, tags]);

	// 검색 실행 (autocomplete 지원)
	const executeSearch = useCallback(
		(searchValue, selectedItem) => {
			const searchParams = new URLSearchParams();
			const currentPath = location.pathname;

			if (searchValue.trim()) {
				// 선택된 아이템이 태그인 경우 태그로 검색, 아니면 제목으로 검색
				if (selectedItem?.type === 'tag') {
					// 태그 검색 로직 (기존 코드와 통합)
					const tagId = selectedItem.data?.uuid || selectedItem.id;
					// 태그 클릭 핸들러 사용
					console.log('selectedItem', selectedItem);
					const curTagItem = {
						name: selectedItem.data?.title,
						id: selectedItem.data?.uuid,
					};
					tagClickHandler(curTagItem, 'search');
					return;
				} else {
					searchParams.append('searchTitle', searchValue);
				}

				navigate(`${currentPath}?${searchParams.toString()}`);
				closeAutocomplete();
			}
		},
		[location.pathname, navigate, tagClickHandler],
	);

	// 자동완성 닫기
	const closeAutocomplete = useCallback(() => {
		autocomplete.setIsOpen(false);
		autocomplete.setSelectedIndex(-1);
	}, [autocomplete]);

	// 새로운 핸들러들
	const handleInputChange = (e) => {
		const value = e.target.value;
		setSearchInput(value);

		// ref와 state 동기화
		if (searchInputRef.current) {
			searchInputRef.current.value = value;
		}

		// 기존 특수문자 필터링 로직 유지
		const regex = /[!@#$%^&()\-_+={}:;'"<>\\/?.,,]/g;
		if (regex.test(value) && value?.length === 1) {
			alert('특수문자부터 입력은 불가능합니다.');
			const cleanValue = value.replace(regex, '');
			e.target.value = cleanValue;
			setSearchInput(cleanValue);
			if (searchInputRef.current) {
				searchInputRef.current.value = cleanValue;
			}
		}
	};

	const handleCompositionStart = useCallback(() => {
		setIsComposing(true);
	}, []);

	const handleCompositionEnd = useCallback(() => {
		setIsComposing(false);
	}, []);

	const handleKeyDown = (e) => {
		if (isComposing && e.key === 'Enter') return;

		autocomplete.handleKeyDown(e);

		if (e.key === 'Enter') {
			e.preventDefault();
			const selectedItem = autocomplete.getSelectedItem();
			const searchValue = selectedItem ? selectedItem.value : searchInput;

			// 검색 기록에 추가 (search 함수 호출)
			if (searchValue.trim()) {
				// searchInputRef와 searchInput 동기화
				if (searchInputRef.current) {
					searchInputRef.current.value = searchValue;
				}
				setSearchInput(searchValue);

				// 검색 기록에 추가
				search();
			}

			executeSearch(searchValue, selectedItem);
			closeAutocomplete();

			if (e.currentTarget) {
				e.currentTarget.blur();
			}
		}
	};

	const handleAutocompleteSelect = (item) => {
		setSearchInput(item.value);

		// searchInputRef 동기화
		if (searchInputRef.current) {
			searchInputRef.current.value = item.value;
		}

		// 검색 기록에 추가
		search();

		executeSearch(item.value, item);
		closeAutocomplete();
	};

	const handleFocus = useCallback(() => {
		clearTimeout(blurTimeoutId);
		setSearchInputFocus(true);

		if (
			debouncedSearchText.length >= 2 &&
			autocomplete.filteredItems.length > 0
		) {
			autocomplete.setIsOpen(true);
		}
	}, [debouncedSearchText, autocomplete, blurTimeoutId]);

	const handleBlur = useCallback(() => {
		const timeoutId = setTimeout(() => {
			setSearchInputFocus(false);
			closeAutocomplete();
		}, 200);
		setBlurTimeoutId(timeoutId);
	}, [closeAutocomplete]);
	// 사용자 로그인 이벤트 처리
	const handleUserLogin = () => {
		// 사용자가 로그인하면 쿠키에서 검색어 목록 가져오기
		try {
			const searchWordHistoryCookie = Cookie.get('searchWordListHistory');
			const pfName = Cookie.get('profile').name;
			const pfWordHistory = searchWordHistoryCookie[pfName];

			if (pfWordHistory && pfWordHistory.length > 0) {
				setSearchWordList(pfWordHistory);
			}
		} catch (error) {
			console.log('쿠키 정보 불러오기 실패 : ' + error.message);
		}
	};

	// 프로필 이름 저장
	useEffect(() => {
		const pfCookie = Cookie.get('profile');
		const pfName = pfCookie.name;

		setProfileName(pfName);
	}, []);

	// 페이지 로드 시 쿠키에서 검색어 목록 가져오기
	useEffect(() => {
		try {
			const profileCookie = Cookie.get('profile');
			const collectionNameCookie = Cookie.get('collectionName');

			if (profileCookie && collectionNameCookie) {
				setIsCookieHere(true);
				handleUserLogin();
			}
		} catch (error) {
			console.log('쿠키 정보 불러오기 실패 : ', error.message);
		} finally {
			// console.log('쿠키 정보 불러오기 작업 끝');
		}
	}, []);

	// 검색어 입력 시 주소창 업데이트
	useEffect(() => {
		updateSearchTitle();
	}, [searchWordList, currentSearchWord]);

	// 검색어 목록 변경 시 쿠키에 반영
	useEffect(() => {
		updateSearchWordListFromCookie();
	}, [searchWordList]);

	// 검색어 추가 (기존 로직 + autocomplete 통합)
	const search = () => {
		setSearchWordList((state) => {
			setIsSearchBarClicked(false);
			let newSearchWord = searchInput.trim(); // state를 우선 사용

			// state가 없으면 ref에서 가져오기
			if (!newSearchWord && searchInputRef.current) {
				newSearchWord = searchInputRef.current.value.trim();
			}

			if (newSearchWord?.length) {
				const uniqueSearchWordsSet = new Set([...state, newSearchWord]);
				const uniqueSearchWordsAry = Array.from(uniqueSearchWordsSet);

				setCurrentSearchWord(newSearchWord);

				// ref와 state 동기화
				setSearchInput(newSearchWord);
				if (searchInputRef.current) {
					searchInputRef.current.value = newSearchWord;
				}

				updateSearchTitle();

				return uniqueSearchWordsAry;
			} else {
				return state;
			}
		});
	};

	// 검색어 삭제
	const removeSearchInput = (_index) => {
		if (!searchInputRef.current) return;
		if (_index === undefined || _index === null) {
			searchInputRef.current.value = '';
			setSearchInput(''); // 새로 추가
		} else {
			const tempList = [...searchWordList];
			tempList.splice(_index, 1);
			setSearchWordList(tempList);
			setCurrentSearchWord(null);
			updateSearchTitle();
		}
	};

	// 검색어 목록 업데이트
	const updateSearchWordListFromCookie = () => {
		if (isCookieHere && searchWordList) {
			const existingSearchWordListHistory =
				wordHistoryCookie.searchWordListHistory || {};

			const newSearchWordListHistory = {
				...existingSearchWordListHistory,
				[profileName]: searchWordList,
			};

			SetWordHistoryCookie(newSearchWordListHistory);
			console.log('newSearchWordListHistory', newSearchWordListHistory);
			Cookie.set(
				'searchWordListHistory',
				newSearchWordListHistory,
				'{expires: 7 }',
			);
		}
	};

	// 주소창 업데이트
	const updateSearchTitle = () => {
		const searchParams = new URLSearchParams();
		const currentPath = location.pathname;

		if (!(searchWordList?.length && currentSearchWord)) return;

		searchParams.append('searchTitle', currentSearchWord);
		navigate(`${currentPath}?${searchParams.toString()}`);
	};

	// 검색어 목록 아이템 클릭 핸들러
	const handleWordHistoryItemClick = (itemIndex) => {
		const word = searchWordList[itemIndex];

		searchInputRef.current.value = word;
		setSearchInput(word); // 새로 추가
		setCurrentSearchWord(word);
		updateSearchTitle();
	};

	// 검색 기록을 모두 지우기
	const deleteAllHistoryWords = () => {
		let answer = window.confirm('검색 기록을 모두 지울까요?');

		if (answer) {
			if (searchWordList.length > 0) {
				setSearchWordList([]);
			}
		}
	};

	// 검색어 입력창에서 엔터키 입력 감지
	useEnterKeyEvent({
		elementRef: searchInputRef,
		functionToExecute: search,
	});

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				searchBarRef.current &&
				!searchBarRef.current.contains(event.target)
			) {
				setIsSearchBarClicked(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [searchBarRef]);

	// AutocompleteDropdown 닫힘 감지용 ref
	const autocompleteDropdownRef = useRef(null);
	useClickOutside({
		elementRef: autocompleteDropdownRef,
		stateVariable: autocomplete.isOpen,
		stateFunction: autocomplete.setIsOpen,
	});

	if (playRegex.test(location.pathname) || location.pathname === '/main') {
		return (
			<S.Container ref={searchBarRef}>
				{/* 1. 검색창 */}
				<S.SeachInputContainer style={{ position: 'relative' }}>
					<S.SearchBarWrapper>
						<S.SearchInput
							placeholder='검색어를 입력하세요'
							ref={searchInputRef}
							value={searchInput}
							onFocus={handleFocus}
							onBlur={handleBlur}
							onClick={() => setIsSearchBarClicked(true)}
							onChange={handleInputChange}
							onKeyDown={handleKeyDown}
							onCompositionStart={handleCompositionStart}
							onCompositionEnd={handleCompositionEnd}
							autoComplete='off'
						/>
						<S.SearchButton
							onClick={search}
							onMouseEnter={(e) => e.target.classList.add('hovered')}
							onMouseLeave={(e) => e.target.classList.remove('hovered')}>
							<Image srcArray={S.search_list} />
						</S.SearchButton>
					</S.SearchBarWrapper>

					{/* 로딩 인디케이터 */}
					{isLoading && searchInput.length >= 2 && (
						<div
							style={{
								position: 'absolute',
								right: '40px',
								top: '50%',
								transform: 'translateY(-50%)',
								fontSize: '12px',
								color: '#666',
								zIndex: 1001,
							}}>
							검색 중...
						</div>
					)}

					{/* 에러 표시 */}
					{error && searchInput.length >= 2 && (
						<div
							style={{
								position: 'absolute',
								right: '40px',
								top: '50%',
								transform: 'translateY(-50%)',
								fontSize: '12px',
								color: '#e74c3c',
								zIndex: 1001,
							}}>
							검색 실패
						</div>
					)}

					{/* Autocomplete 드롭다운 */}
					<AutocompleteDropdown
						items={autocomplete.filteredItems}
						selectedIndex={autocomplete.selectedIndex}
						onItemClick={handleAutocompleteSelect}
						onItemHover={autocomplete.handleItemHover}
						isVisible={
							autocomplete.isOpen &&
							autocomplete.filteredItems.length > 0 &&
							!isLoading
						}
						style={{
							zIndex: 10000, // 높은 z-index 설정
							// SearchBarWrapper와 동일한 너비 responsive 적용
							maxWidth: 'inherit',
							borderRadius: '1.2rem', // 검색창보다 살짝 작은 둥근 모서리
							marginTop: '0.2rem',
						}}
						ref={autocompleteDropdownRef}
					/>
				</S.SeachInputContainer>

				{/* 2. 검색 상세 정보 창 */}
				<S.SearchDetailBoxWrapper>
					<SearchDetailBox
						isDetailPanelOn={isSearchBarClicked}
						searchWordList={searchWordList}
						handleWordHistoryItemClick={handleWordHistoryItemClick}
						removeSearchInput={removeSearchInput}
						deleteAllHistoryWords={deleteAllHistoryWords}
					/>
					<S.BlackBackground
						isDetailPanelOn={isSearchBarClicked}
						onMouseDown={() => setIsSearchBarClicked(false)}
					/>
				</S.SearchDetailBoxWrapper>
			</S.Container>
		);
	} else if (location.pathname === '/') {
		// return <S.PortalWord>PORTAL</S.PortalWord>;
		return <></>;
	}
});

export default SearchComponent;
